import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FiArrowLeft, FiArrowRight, FiCpu, FiLock, FiMail, FiUser, FiX } from 'react-icons/fi'
import { FcGoogle } from 'react-icons/fc'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import Card from './ui/Card'
import Button from './ui/Button'
import {
  getAuthFieldErrors,
  validateLoginForm,
  validateRegisterEmail,
  validateResetPasswordEmail,
} from '../utils/authErrors'
import LegalConsentText from './LegalConsentText'
import { checkDeviceAvailability, normalizeDeviceId } from '../utils/deviceId'

const FieldError = ({ message }) =>
  message ? <p className="mt-1 text-[11px] text-red-500 dark:text-red-400 font-medium">{message}</p> : null

const FIELD = 'input-field !min-h-[42px] !py-2.5 !pl-[3rem] !text-sm'
const ICON = 'absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none'

const AuthModal = ({ onClose }) => {
  const { login, register, loginWithGoogle, resetPassword } = useAuth()
  const [mode, setMode] = useState('login')
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', deviceId: '' })
  const [fieldErrors, setFieldErrors] = useState({})
  const [registerStep, setRegisterStep] = useState(1)
  const [acceptedLegal, setAcceptedLegal] = useState(false)
  const focusRef = useRef(null)

  useEffect(() => {
    const raf = requestAnimationFrame(() => focusRef.current?.focus?.({ preventScroll: true }))
    return () => cancelAnimationFrame(raf)
  }, [mode, registerStep])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    if (mode !== 'register') {
      setRegisterStep(1)
      setAcceptedLegal(false)
    }
    setFieldErrors({})
  }, [mode])

  const patchForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }))
    const keys = Object.keys(patch)
    setFieldErrors((prev) => {
      const next = { ...prev }
      keys.forEach((k) => {
        if (next[k]) delete next[k]
      })
      return next
    })
  }

  const passwordRules = useMemo(() => ({
    len: form.password.length >= 8,
    upper: /[A-Z]/.test(form.password),
    lower: /[a-z]/.test(form.password),
    num: /\d/.test(form.password),
    special: /[^A-Za-z0-9]/.test(form.password),
  }), [form.password])

  const passwordScore = useMemo(() => Object.values(passwordRules).filter(Boolean).length, [passwordRules])
  const passwordAllOk = passwordScore === 5

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setFieldErrors({})
    try {
      if (mode === 'login') {
        const validation = validateLoginForm({ email: form.email, password: form.password })
        if (!validation.ok) {
          setFieldErrors(validation.errors)
          const first = validation.errors.email || validation.errors.password
          if (first) toast.error(first)
          return
        }
        await login(validation.email, form.password)
        toast.success('Login realizado com sucesso!')
        onClose()
        return
      }

      if (!form.name.trim()) {
        setFieldErrors({ name: 'Informe seu nome.' })
        toast.error('Informe seu nome.')
        return
      }
      const emailCheck = validateRegisterEmail(form.email)
      if (!emailCheck.ok) {
        setFieldErrors(emailCheck.errors)
        toast.error(emailCheck.errors.email)
        return
      }
      if (registerStep !== 2) {
        toast.error('Finalize a etapa de senha.')
        return
      }
      if (form.password !== form.confirmPassword) {
        setFieldErrors({ confirmPassword: 'As senhas não coincidem.' })
        toast.error('As senhas não coincidem.')
        return
      }
      if (!passwordAllOk) {
        setFieldErrors({ password: 'Sua senha não atende aos requisitos de segurança.' })
        toast.error('Sua senha não atende aos requisitos de segurança.')
        return
      }
      if (!acceptedLegal) {
        toast.error('Aceite a Política de Privacidade e os Termos de Uso para criar sua conta.')
        return
      }
      const deviceId = normalizeDeviceId(form.deviceId)
      if (!deviceId) {
        setFieldErrors({ deviceId: 'Informe o ID do dispositivo.' })
        toast.error('Informe o ID do dispositivo para criar a conta.')
        return
      }

      const availability = await checkDeviceAvailability(deviceId)
      if (!availability.available) {
        setFieldErrors({ deviceId: availability.message })
        toast.error(availability.message)
        return
      }

      await register({
        name: form.name.trim(),
        email: emailCheck.email,
        password: form.password,
        deviceId,
      })
      toast.success('Conta criada e dispositivo vinculado!')
      onClose()
    } catch (error) {
      console.error('Erro de autenticação:', error?.code || error)
      const mapped = getAuthFieldErrors(error)
      if (Object.keys(mapped).length > 0) setFieldErrors((prev) => ({ ...prev, ...mapped }))
      toast.error(error.message || 'Não foi possível completar a operação. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    setFieldErrors({})
    const validation = validateResetPasswordEmail(form.email)
    if (!validation.ok) {
      setFieldErrors(validation.errors)
      toast.error(validation.errors.email)
      return
    }
    try {
      setLoading(true)
      await resetPassword(validation.email)
      toast.success('Se o e-mail existir, enviaremos instruções para redefinir sua senha.')
    } catch (error) {
      console.error('Erro ao recuperar senha:', error?.code || error)
      const mapped = getAuthFieldErrors(error)
      if (Object.keys(mapped).length > 0) setFieldErrors((prev) => ({ ...prev, ...mapped }))
      toast.error(error.message || 'Não foi possível enviar o e-mail de recuperação.')
    } finally {
      setLoading(false)
    }
  }

  const goToPasswordStep = () => {
    if (!form.name.trim()) {
      setFieldErrors({ name: 'Informe seu nome.' })
      return toast.error('Informe seu nome.')
    }
    const emailCheck = validateRegisterEmail(form.email)
    if (!emailCheck.ok) {
      setFieldErrors(emailCheck.errors)
      return toast.error(emailCheck.errors.email)
    }
    const deviceId = normalizeDeviceId(form.deviceId)
    if (!deviceId) {
      setFieldErrors({ deviceId: 'Informe o ID do dispositivo.' })
      return toast.error('Informe o ID do dispositivo para criar a conta.')
    }
    setFieldErrors({})
    setRegisterStep(2)
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />

      <Card
        padding="md"
        hover={false}
        className="relative max-w-md w-full min-w-0 z-10 !p-5 sm:!p-6 max-h-[min(92vh,720px)] overflow-y-auto"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none"
          aria-label="Fechar login"
        >
          <FiX className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>

        <div className="mb-3 pr-8">
          <p className="text-[10px] font-bold text-accent uppercase tracking-[0.2em] mb-1">Acesso</p>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {mode === 'login' ? 'Entrar na sua conta' : 'Criar uma conta'}
          </h2>
          {mode === 'register' && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 leading-snug">
              Cadastro com ID do dispositivo Sys[mv]. Se comprou e ainda não chegou, aguarde o aparelho.
            </p>
          )}
        </div>

        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => setMode('login')}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold border ${
              mode === 'login'
                ? 'border-accent bg-accent text-white'
                : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-transparent'
            }`}
          >
            Entrar
          </button>
          <button
            type="button"
            onClick={() => setMode('register')}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold border ${
              mode === 'register'
                ? 'border-accent bg-accent text-white'
                : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-transparent'
            }`}
          >
            Cadastrar
          </button>
        </div>

        {mode === 'register' && (
          <div className="flex items-center gap-2 mb-3 text-xs font-semibold text-gray-500 dark:text-gray-400">
            <span className={registerStep === 1 ? 'text-accent' : ''}>1. Dados</span>
            <span className="text-gray-300 dark:text-gray-600">→</span>
            <span className={registerStep === 2 ? 'text-accent' : ''}>2. Senha</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-2.5">
          {mode === 'register' && registerStep === 1 && (
            <>
              <div>
                <label className="form-label !mb-1 !text-xs">Nome completo</label>
                <div className="relative">
                  <FiUser className={ICON} />
                  <input
                    ref={focusRef}
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => patchForm({ name: e.target.value })}
                    className={`${FIELD} ${fieldErrors.name ? 'border-red-400 dark:border-red-500' : ''}`}
                    placeholder="Seu nome"
                    autoComplete="name"
                  />
                </div>
                <FieldError message={fieldErrors.name} />
              </div>

              <div>
                <label className="form-label !mb-1 !text-xs">E-mail</label>
                <div className="relative">
                  <FiMail className={ICON} />
                  <input
                    type="email"
                    inputMode="email"
                    value={form.email}
                    onChange={(e) => patchForm({ email: e.target.value })}
                    className={`${FIELD} ${fieldErrors.email ? 'border-red-400 dark:border-red-500' : ''}`}
                    placeholder="seu@email.com"
                    autoComplete="email"
                  />
                </div>
                <FieldError message={fieldErrors.email} />
              </div>

              <div>
                <label className="form-label !mb-1 !text-xs">ID do dispositivo</label>
                <div className="relative">
                  <FiCpu className={ICON} />
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    value={form.deviceId}
                    onChange={(e) => patchForm({ deviceId: e.target.value })}
                    className={`${FIELD} ${fieldErrors.deviceId ? 'border-red-400 dark:border-red-500' : ''}`}
                    placeholder="Ex: 123456789"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
                <FieldError message={fieldErrors.deviceId} />
                <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300 leading-snug">
                  Obrigatório. ID numérico da etiqueta/QR do aparelho.
                </p>
              </div>
            </>
          )}

          {mode === 'register' && registerStep === 2 && (
            <>
              <div>
                <label className="form-label !mb-1 !text-xs">Senha</label>
                <div className="relative">
                  <FiLock className={ICON} />
                  <input
                    ref={focusRef}
                    type="password"
                    required
                    value={form.password}
                    onChange={(e) => patchForm({ password: e.target.value })}
                    className={`${FIELD} ${fieldErrors.password ? 'border-red-400 dark:border-red-500' : ''}`}
                    placeholder="Crie uma senha forte"
                    autoComplete="new-password"
                  />
                </div>
                <FieldError message={fieldErrors.password} />
              </div>

              <div>
                <label className="form-label !mb-1 !text-xs">Confirmar senha</label>
                <div className="relative">
                  <FiLock className={ICON} />
                  <input
                    type="password"
                    required
                    value={form.confirmPassword}
                    onChange={(e) => patchForm({ confirmPassword: e.target.value })}
                    className={`${FIELD} ${fieldErrors.confirmPassword ? 'border-red-400 dark:border-red-500' : ''}`}
                    placeholder="Repita a senha"
                    autoComplete="new-password"
                  />
                </div>
                <FieldError message={fieldErrors.confirmPassword} />
              </div>

              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2.5">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <p className="text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Segurança
                  </p>
                  <span className={`text-[11px] font-extrabold ${passwordAllOk ? 'text-accent' : 'text-gray-500'}`}>
                    {passwordScore}/5
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-accent transition-all duration-300"
                    style={{ width: `${(passwordScore / 5) * 100}%` }}
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                  8+ chars, maiúscula, minúscula, número e especial.
                </p>
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedLegal}
                  onChange={(e) => setAcceptedLegal(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-accent"
                />
                <span className="text-[11px] text-gray-600 dark:text-gray-400 leading-snug">
                  <LegalConsentText onLinkClick={onClose} />
                </span>
              </label>
            </>
          )}

          {mode === 'login' && (
            <>
              <div>
                <label className="form-label !mb-1 !text-xs">E-mail</label>
                <div className="relative">
                  <FiMail className={ICON} />
                  <input
                    ref={focusRef}
                    type="email"
                    inputMode="email"
                    value={form.email}
                    onChange={(e) => patchForm({ email: e.target.value })}
                    className={`${FIELD} ${fieldErrors.email ? 'border-red-400 dark:border-red-500' : ''}`}
                    placeholder="seu@email.com"
                    autoComplete="email"
                  />
                </div>
                <FieldError message={fieldErrors.email} />
              </div>

              <div>
                <label className="form-label !mb-1 !text-xs">Senha</label>
                <div className="relative">
                  <FiLock className={ICON} />
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => patchForm({ password: e.target.value })}
                    className={`${FIELD} ${fieldErrors.password ? 'border-red-400 dark:border-red-500' : ''}`}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
                <FieldError message={fieldErrors.password} />
              </div>

              <Button type="submit" variant="primary" size="md" className="w-full mt-1" disabled={loading}>
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Entrando...
                  </>
                ) : (
                  <>
                    <FiUser className="w-4 h-4 mr-2" />
                    Entrar
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading}
                className="w-full text-xs font-semibold text-accent hover:underline disabled:opacity-50"
              >
                Esqueci minha senha
              </button>
            </>
          )}

          {mode === 'register' && (
            <div className="grid grid-cols-2 gap-2 pt-1">
              {registerStep === 2 ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-full gap-1.5 !min-h-[42px]"
                  disabled={loading}
                  onClick={() => setRegisterStep(1)}
                >
                  <FiArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-full gap-1.5 !min-h-[42px]"
                  disabled={loading}
                  onClick={onClose}
                >
                  Agora não
                </Button>
              )}

              {registerStep === 1 ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="w-full gap-1.5 !min-h-[42px]"
                  disabled={loading}
                  onClick={goToPasswordStep}
                >
                  Continuar
                  <FiArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button type="submit" variant="primary" size="sm" className="w-full gap-1.5 !min-h-[42px]" disabled={loading}>
                  {loading ? 'Criando...' : 'Criar conta'}
                </Button>
              )}
            </div>
          )}
        </form>

        <div className="mt-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">
              ou
            </span>
            <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>

          {mode === 'register' && registerStep === 1 && (
            <label className="flex items-start gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={acceptedLegal}
                onChange={(e) => setAcceptedLegal(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-accent"
              />
              <span className="text-[11px] text-gray-600 dark:text-gray-400 leading-snug">
                <LegalConsentText onLinkClick={onClose} />
              </span>
            </label>
          )}

          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="w-full gap-2 !min-h-[42px]"
            disabled={loading}
            onClick={async () => {
              if (mode === 'register') {
                if (!acceptedLegal) {
                  toast.error('Aceite a Política de Privacidade e os Termos de Uso para continuar.')
                  return
                }
                const deviceId = normalizeDeviceId(form.deviceId)
                if (!deviceId) {
                  setFieldErrors({ deviceId: 'Informe o ID do dispositivo.' })
                  toast.error('Informe o ID do dispositivo para criar a conta com Google.')
                  return
                }
                try {
                  setLoading(true)
                  const availability = await checkDeviceAvailability(deviceId)
                  if (!availability.available) {
                    setFieldErrors({ deviceId: availability.message })
                    toast.error(availability.message)
                    return
                  }
                  await loginWithGoogle({ deviceId, intent: 'register' })
                  toast.success('Conta criada e dispositivo vinculado!')
                  onClose()
                } catch (error) {
                  console.error('Erro ao cadastrar com Google:', error?.code || error)
                  toast.error(error.message || 'Não foi possível criar a conta com Google.')
                } finally {
                  setLoading(false)
                }
                return
              }

              try {
                setLoading(true)
                await loginWithGoogle({ intent: 'login' })
                toast.success('Login com Google realizado com sucesso!')
                onClose()
              } catch (error) {
                console.error('Erro ao entrar com Google:', error?.code || error)
                toast.error(error.message || 'Não foi possível entrar com Google. Tente novamente.')
              } finally {
                setLoading(false)
              }
            }}
          >
            <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
              <FcGoogle className="w-4 h-4" />
            </span>
            {mode === 'register' ? 'Cadastrar com Google' : 'Entrar com Google'}
          </Button>

          {mode === 'register' && registerStep === 1 && (
            <p className="mt-2 text-center text-[11px] text-gray-500 dark:text-gray-400">
              Ao continuar, você aceita a{' '}
              <Link to="/privacidade" onClick={onClose} className="text-accent font-semibold hover:underline">
                Privacidade
              </Link>{' '}
              e os{' '}
              <Link to="/termos" onClick={onClose} className="text-accent font-semibold hover:underline">
                Termos
              </Link>
              .
            </p>
          )}
        </div>
      </Card>
    </div>
  )
}

export default AuthModal
